/**
 * Publishes the manifest registry's decision to `window.blink.microfrontends`
 * in the legacy shape still read by @thoughtspot/i18n, the assets app,
 * radiant illustration-utils and the debug banner. Strict replace, never a
 * merge. Assets loading must finish before the first initializeAppLocale().
 */
import { create } from '@thoughtspot/logger';
import { getMfeAssetUrl } from '@thoughtspot/microfrontends';
import type { MfeRegistryState } from './mfe-registry';

declare const window: any;

const logger = create('mfe-window-bridge');

export const ASSETS_APP = 'assets';
const ASSETS_SCRIPT_TIMEOUT_MS = 5000;

/** Payload the inline bootstrap left on the window before we replaced it. */
let legacyPayload: Record<string, any> | undefined;
let assetsLoad: Promise<void> | null = null;
let publishedFromManifest = false;

/**
 * Where `window.blink.microfrontends` came from. Synchronous by design: the
 * resource lookup in microfrontends.util must not await the registry, so the
 * bootstrap publishes first and every later read is a plain window read.
 */
export const getMfeDeliverySource = (): 'manifest' | 'legacy' =>
    publishedFromManifest ? 'manifest' : 'legacy';

interface LegacyMfeRecord {
    js: string;
    css?: string;
    path: string;
    releaseVersion: string;
    baseVersion: string;
    buildTag: string;
}

/** Remembers the payload it replaces, so loadAssetsFromManifest can tell whether
 *  the bootstrap already ran this assets build. */
export function publishManifestToWindow(state: MfeRegistryState): void {
    if (!state.enabled || !state.manifest || !state.cdnOrigin) {
        return;
    }
    const { manifest, cdnOrigin } = state;
    const previous = window.blink?.microfrontends;
    const published: Record<string, any> = {};
    Object.entries(manifest.mfes).forEach(([name, entry]) => {
        if (!entry) return;
        const record: LegacyMfeRecord = {
            js: getMfeAssetUrl(cdnOrigin, entry.entryPath, entry.js),
            css: entry.css
                ? getMfeAssetUrl(cdnOrigin, entry.entryPath, entry.css)
                : undefined,
            path: entry.entryPath,
            releaseVersion: entry.version,
            baseVersion: entry.version,
            buildTag: entry.version,
        };
        published[name] = record;
    });
    // After the entries, so a manifest key can never shadow them.
    published.isCdnEnabled = true;
    published.cdnBasePath = cdnOrigin;
    window.blink = window.blink || {};
    window.blink.microfrontends = published;
    legacyPayload = previous;
    publishedFromManifest = true;
}

/**
 * The inline bootstrap inserts the SVG sprite as a hidden <div> at the top of
 * <body>; drop it so `<use href="#id">` resolves against the new sprite.
 */
function removeLegacySprite(): void {
    const stale = Array.from(document.body?.children ?? []).find(
        el =>
            el.tagName === 'DIV' &&
            (el as HTMLElement).style.display === 'none' &&
            el.querySelector('svg symbol'),
    );
    stale?.remove();
}

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        if (window.blink?.nonce) {
            script.nonce = window.blink.nonce;
        }
        const timer = setTimeout(() => {
            script.remove();
            reject(new Error(`timed out after ${ASSETS_SCRIPT_TIMEOUT_MS}ms`));
        }, ASSETS_SCRIPT_TIMEOUT_MS);
        script.onload = () => {
            clearTimeout(timer);
            resolve();
        };
        script.onerror = () => {
            clearTimeout(timer);
            script.remove();
            reject(new Error('failed to load'));
        };
        document.head.appendChild(script);
    });
}

/**
 * Loads the manifest's `assets` app, which populates
 * `assets.{translations,svgIcons}` and injects the SVG sprite. Idempotent;
 * failure is non-fatal (i18n falls back to bundled strings).
 */
async function loadAssetsOnce(): Promise<void> {
    const assets = window.blink?.microfrontends?.[ASSETS_APP];
    if (!assets?.js) {
        return;
    }
    const legacyAssets = legacyPayload?.[ASSETS_APP];
    if (legacyAssets?.js === assets.js && legacyAssets.translations) {
        // Same build already initialised by the bootstrap; reuse its result.
        assets.translations = legacyAssets.translations;
        assets.svgIcons = legacyAssets.svgIcons;
        return;
    }
    if (legacyAssets?.js && legacyAssets.js !== assets.js) {
        removeLegacySprite();
    }
    try {
        await loadScript(assets.js);
        logger.info(`assets app loaded from manifest (${assets.buildTag})`);
    } catch (error) {
        logger.error(
            `assets app ${assets.js} ${error.message}; using bundled strings/icons`,
        );
    }
}

export function loadAssetsFromManifest(state: MfeRegistryState): Promise<void> {
    // Only act on a payload we published; with no manifest the window still
    // holds the legacy payload whose assets build the bootstrap already ran.
    if (!state.enabled || !state.manifest) {
        return Promise.resolve();
    }
    if (!assetsLoad) {
        assetsLoad = loadAssetsOnce();
    }
    return assetsLoad;
}

/** Test-only. */
export function resetMfeWindowBridgeForTests(): void {
    legacyPayload = undefined;
    assetsLoad = null;
    publishedFromManifest = false;
}
